import React, { useEffect, useState, useCallback } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

// Dark in both themes, in colors the light-mode remaps in index.css never touch, so
// the buttons stay light on dark and visible without hover (as the attachment viewer's).
const BAR_BUTTON = 'p-2 rounded-lg text-white/90 bg-white/15 ring-1 ring-inset ring-white/20 hover:bg-white/25';

export default function ScreenshotLightbox({ items = [], index, onClose, onNavigate }) {
  const { t } = useTranslation('common');
  const [zoom, setZoom] = useState(false);
  const current = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  const goPrev = useCallback(() => { if (hasPrev) onNavigate(index - 1); }, [hasPrev, index, onNavigate]);
  const goNext = useCallback(() => { if (hasNext) onNavigate(index + 1); }, [hasNext, index, onNavigate]);

  useEffect(() => { setZoom(false); }, [index]);

  useEffect(() => {
    if (!current) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') { e.stopImmediatePropagation(); goPrev(); }
      else if (e.key === 'ArrowRight') { e.stopImmediatePropagation(); goNext(); }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [current, goPrev, goNext]);

  if (!current) return null;

  return (
    <DialogPrimitive.Root open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[9999] bg-black/95 animate-in fade-in" />
        <DialogPrimitive.Content
          className="bb-always-dark fixed inset-0 z-[9999] flex flex-col outline-none"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={onClose}
        >
          <DialogPrimitive.Title className="sr-only">{current.alt || t('ui_common.screenshot_preview')}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">{current.caption || ''}</DialogPrimitive.Description>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0 flex-1 mr-4">
              {items.length > 1 && (
                <span className="text-xs text-white/50 flex-shrink-0">{index + 1} / {items.length}</span>
              )}
              <span className="text-sm text-white/80 truncate">{current.alt || ''}</span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {hasPrev && (
                <button onClick={goPrev} className={BAR_BUTTON} aria-label={t('common.previous')}>
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              {hasNext && (
                <button onClick={goNext} className={BAR_BUTTON} aria-label={t('common.next')}>
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
              <a href={current.src} target="_blank" rel="noreferrer" className={BAR_BUTTON} aria-label={t('common.open_original')}>
                <ExternalLink className="w-4 h-4" />
              </a>
              <button onClick={onClose} className={BAR_BUTTON} aria-label={t('common.close')}>
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Image area */}
          <div className="flex-1 overflow-auto flex items-center justify-center p-4 sm:p-8 relative" onClick={onClose}>
            {hasPrev && (
              <button onClick={(e) => { e.stopPropagation(); goPrev(); }} className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/80 text-white/90 rounded-full z-10" aria-label={t('common.previous')}>
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            {hasNext && (
              <button onClick={(e) => { e.stopPropagation(); goNext(); }} className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/80 text-white/90 rounded-full z-10" aria-label={t('common.next')}>
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
            <img
              src={current.src}
              alt={current.alt || ''}
              onClick={(e) => { e.stopPropagation(); setZoom((z) => !z); }}
              className={zoom
                ? 'max-w-none cursor-zoom-out rounded-lg shadow-2xl'
                : 'max-w-full max-h-full object-contain cursor-zoom-in rounded-lg shadow-2xl'
              }
              style={zoom ? { width: '160%' } : undefined}
              draggable={false}
            />
          </div>

          {current.caption && (
            <div className="px-4 py-3 border-t border-white/10 text-center text-sm text-white/60 flex-shrink-0">
              {current.caption}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
